import { useMemo } from "react";
import { useAdminData } from "../AdminDataContext";
import { newsStore, activityStore, voteStore } from "../utils/dataStore";

interface Props {
  onNavigate: (section: string) => void;
}

export default function AdminDashboard({ onNavigate }: Props) {
  const { memberApplications, registrations, members, storeVersion } = useAdminData();
  const pendingMembers = memberApplications.slice(0, 3);
  const pendingRegs = registrations.filter((r) => r.status === "pending").slice(0, 3);

  const pendingMemberCount = memberApplications.length;
  const pendingRegCount = registrations.filter((r) => r.status === "pending").length;
  const activityList = activityStore.getAll();
  const ongoingActivityCount = activityList.filter((a) => a.status === "ongoing").length;
  const newsList = newsStore.getAll();
  const pendingNewsCount = newsList.filter((n) => n.status === "pending").length;

  const statsCards = useMemo(() => {
    const thisMonth = new Date().getMonth();
    const thisYear = new Date().getFullYear();
    const activitiesThisMonth = activityStore.getAll().filter((a) => {
      const d = Date.parse(a.date);
      if (Number.isNaN(d)) return false;
      const x = new Date(d);
      return x.getMonth() === thisMonth && x.getFullYear() === thisYear;
    }).length;
    return [
      {
        label: "注册会员总数",
        value: members.length.toLocaleString(),
        change: "API",
        changeLabel: "同步",
        icon: "ri-group-3-line",
        color: "bg-red-50 text-red-600",
        trend: "up",
      },
      {
        label: "待审核会员",
        value: String(pendingMemberCount),
        change: pendingMemberCount > 0 ? "待处理" : "—",
        changeLabel: "",
        icon: "ri-user-received-line",
        color: "bg-amber-50 text-amber-600",
        trend: pendingMemberCount > 0 ? "warn" : "up",
      },
      {
        label: "本月活动数",
        value: String(activitiesThisMonth),
        change: "当月",
        changeLabel: "场次",
        icon: "ri-calendar-event-line",
        color: "bg-green-50 text-green-600",
        trend: "up",
      },
      {
        label: "待审核报名",
        value: String(pendingRegCount),
        change: pendingRegCount > 0 ? "待处理" : "—",
        changeLabel: "",
        icon: "ri-file-list-3-line",
        color: "bg-orange-50 text-orange-600",
        trend: pendingRegCount > 0 ? "warn" : "up",
      },
    ];
  }, [members.length, pendingMemberCount, pendingRegCount, storeVersion]);

  const engagementStats = useMemo(() => {
    const votes = voteStore.getAll().filter((v) => v.status !== "draft");
    const voteVoters = votes.reduce((s, v) => s + (v.totalVoters ?? 0), 0);
    const publishedNews = newsStore.getAll().filter((n) => n.status === "published").length;
    const regTotal = registrations.length;
    const activeMembers = members.filter((m) => m.status === "active").length;
    const activePct = members.length ? Math.round((activeMembers / members.length) * 100) : 0;
    return [
      {
        label: "投票",
        value: String(votes.length),
        sub: "非草稿场次",
        detail: `累计参与人次 ${voteVoters}`,
        icon: "ri-survey-line",
        color: "from-red-500 to-red-400",
        bg: "bg-red-50",
        textColor: "text-red-600",
        progress: votes.length > 0 ? 100 : 0,
        changeLabel: "",
        changeSub: "",
      },
      {
        label: "已发布资讯",
        value: String(publishedNews),
        sub: "当前租户",
        detail: `待审核 ${pendingNewsCount} 条`,
        icon: "ri-newspaper-line",
        color: "from-green-500 to-green-400",
        bg: "bg-green-50",
        textColor: "text-green-600",
        progress: publishedNews > 0 ? 100 : 0,
        changeLabel: "",
        changeSub: "",
      },
      {
        label: "活跃会员占比",
        value: `${activePct}%`,
        sub: "status=active",
        detail: `${activeMembers} / ${members.length || 0} 人`,
        icon: "ri-pulse-line",
        color: "from-amber-500 to-amber-400",
        bg: "bg-amber-50",
        textColor: "text-amber-600",
        progress: activePct,
        changeLabel: "",
        changeSub: "",
      },
      {
        label: "活动报名记录",
        value: String(regTotal),
        sub: "报名总条数",
        detail: `待审核 ${pendingRegCount} 条`,
        icon: "ri-checkbox-circle-line",
        color: "from-gray-500 to-gray-400",
        bg: "bg-gray-50",
        textColor: "text-gray-600",
        progress: regTotal > 0 ? 100 : 0,
        changeLabel: "",
        changeSub: "",
      },
    ];
  }, [members, registrations.length, pendingNewsCount, pendingRegCount, storeVersion]);

  return (
    <div className="space-y-6">
      {/* Today's Tasks Card */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-50">
          <div className="w-8 h-8 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="ri-todo-line text-red-600 text-base"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">今日待办</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {new Date().toLocaleDateString("zh-CN", { month: "long", day: "numeric", weekday: "long" })}
            </p>
          </div>
          {(pendingMemberCount + pendingRegCount + pendingNewsCount) > 0 && (
            <div className="ml-auto flex items-center gap-1.5 bg-red-50 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              {pendingMemberCount + pendingRegCount + pendingNewsCount} 项待处理
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-50">
          {[
            {
              label: "待审核会员",
              count: pendingMemberCount,
              icon: "ri-user-received-line",
              color: "text-amber-600",
              bg: "bg-amber-50",
              section: "member-audit",
              urgent: pendingMemberCount > 0,
            },
            {
              label: "待审核报名",
              count: pendingRegCount,
              icon: "ri-file-list-3-line",
              color: "text-orange-600",
              bg: "bg-orange-50",
              section: "registration-audit",
              urgent: pendingRegCount > 0,
            },
            {
              label: "进行中活动",
              count: ongoingActivityCount,
              icon: "ri-calendar-event-line",
              color: "text-green-600",
              bg: "bg-green-50",
              section: "activity-list",
              urgent: false,
            },
            {
              label: "待审核资讯",
              count: pendingNewsCount,
              icon: "ri-newspaper-line",
              color: "text-teal-600",
              bg: "bg-teal-50",
              section: "news-audit",
              urgent: pendingNewsCount > 0,
            },
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => onNavigate(item.section)}
              className="flex flex-col items-center justify-center p-5 hover:bg-gray-50/80 cursor-pointer transition-colors group"
            >
              <div className={`w-11 h-11 flex items-center justify-center rounded-2xl mb-3 ${item.bg} transition-transform group-hover:scale-110`}>
                <i className={`${item.icon} text-xl ${item.color}`} />
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-0.5 flex items-end gap-1">
                {item.count}
                {item.urgent && (
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse mb-1.5 flex-shrink-0" />
                )}
              </p>
              <p className="text-xs text-gray-400">{item.label}</p>
              <p className={`text-[10px] mt-1.5 flex items-center gap-0.5 ${item.urgent ? "text-red-500" : "text-gray-300"}`}>
                {item.urgent ? "需要处理" : "暂无待办"}
                <i className="ri-arrow-right-s-line" />
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-red-100 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${stat.color}`}>
                <i className={`${stat.icon} text-lg`}></i>
              </div>
              {stat.trend === "warn" ? (
                <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-medium">
                  {stat.change}
                </span>
              ) : (
                <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full font-medium">
                  {stat.change} {stat.changeLabel}
                </span>
              )}
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
            <p className="text-xs text-gray-400">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Engagement Stats */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-bold text-gray-700">参与度 &amp; 运营指标</h3>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">租户实时</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {engagementStats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-gray-200 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${stat.bg} ${stat.textColor}`}>
                  <i className={`${stat.icon} text-base`}></i>
                </div>
                {stat.changeLabel ? (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      stat.changeLabel.startsWith("+")
                        ? "bg-green-50 text-green-600"
                        : "bg-red-50 text-red-500"
                    }`}
                  >
                    {stat.changeLabel} {stat.changeSub}
                  </span>
                ) : (
                  <span className="text-xs text-gray-300">—</span>
                )}
              </div>

              <p className="text-2xl font-bold text-gray-900 mb-0.5">{stat.value}</p>
              <p className="text-xs text-gray-400 mb-3">{stat.label}</p>

              {/* Progress bar */}
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${stat.color} transition-all`}
                  style={{ width: `${stat.progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">{stat.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Member Audit */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              待审核会员申请
            </h3>
            <button
              onClick={() => onNavigate("member-audit")}
              className="text-xs text-red-500 hover:text-red-600 cursor-pointer flex items-center gap-1"
            >
              全部 <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {pendingMembers.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-5 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-red-500 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{m.name}</p>
                    <p className="text-xs text-gray-400">{m.city} · {m.level}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onNavigate("member-audit")}
                    className="px-3 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-lg cursor-pointer hover:bg-red-100 transition-colors whitespace-nowrap"
                  >
                    审核
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Registrations */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              待审核活动报名
            </h3>
            <button
              onClick={() => onNavigate("registration-audit")}
              className="text-xs text-red-500 hover:text-red-600 cursor-pointer flex items-center gap-1"
            >
              全部 <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {pendingRegs.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-5 py-3.5">
                <div>
                  <p className="text-sm font-medium text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-400 line-clamp-1">{r.activityTitle}</p>
                </div>
                <button
                  onClick={() => onNavigate("registration-audit")}
                  className="px-3 py-1 bg-orange-50 text-orange-600 text-xs font-medium rounded-lg cursor-pointer hover:bg-orange-100 transition-colors whitespace-nowrap"
                >
                  处理
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            近期活动状态
          </h3>
          <button
            onClick={() => onNavigate("activity-list")}
            className="text-xs text-red-500 hover:text-red-600 cursor-pointer flex items-center gap-1"
          >
            管理活动 <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">活动名称</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">日期</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">报名进度</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">状态</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {activityList.slice(0, 4).map((a) => {
                const fill = Math.round((a.enrolled / a.capacity) * 100);
                return (
                  <tr key={a.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-3 text-gray-800 font-medium">{a.title}</td>
                    <td className="px-4 py-3 text-gray-500">{a.date}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden w-20">
                          <div className="h-full bg-red-500 rounded-full" style={{ width: `${fill}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-500">{a.enrolled}/{a.capacity}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        a.status === "full" ? "bg-red-50 text-red-600" :
                        a.status === "ongoing" ? "bg-green-50 text-green-600" :
                        "bg-gray-100 text-gray-500"
                      }`}>
                        {a.status === "full" ? "已报满" : a.status === "ongoing" ? "报名中" : "草稿"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
